import { useState, useEffect } from "react"
import {
  Card,
  Tabs,
  Switch,
  Button,
  Space,
  message,
  InputNumber,
  Typography,
  Descriptions,
  Tag,
  Spin,
  Alert,
} from "antd"
import {
  MailOutlined,
  SettingOutlined,
} from "@ant-design/icons"
import { useAuth } from "../../contexts/authContext"
import { getAutoStatus, toggleAutoProcessing, updateAutoConfig } from "../../services/QuoteServices"

const { TabPane } = Tabs
const { Title, Text } = Typography

const QuoteProcessingPage = () => {
  const [autoProcessingEnabled, setAutoProcessingEnabled] = useState(false)
  const [isRunning, setIsRunning] = useState(false)
  const [intervalSeconds, setIntervalSeconds] = useState(60)
  const [limitPerRun, setLimitPerRun] = useState(10)
  const [monitoredEmail, setMonitoredEmail] = useState("No configurado")
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const { getToken } = useAuth()

  useEffect(() => {
    fetchStatus()
  }, [])

  const fetchStatus = async () => {
    try {
      setLoading(true)
      const data = await getAutoStatus()

      if (data.success) {
        setAutoProcessingEnabled(data.data.is_enabled)
        setIsRunning(data.data.is_running)
        setIntervalSeconds(data.data.interval_seconds)
        setLimitPerRun(data.data.limit_per_run)
        setMonitoredEmail(data.data.monitored_email || "No configurado")
      }
    } catch (error) {
      console.error("Error fetching status:", error)
      message.error("Error al cargar el estado")
    } finally {
      setLoading(false)
    }
  }

  const handleToggle = async () => {
    try {
      setSaving(true)
      const token = await getToken()
      const data = await toggleAutoProcessing(token)

      if (data.success) {
        setAutoProcessingEnabled(data.is_enabled)
        message.success(data.message)
      } else {
        message.error("Error al alternar el procesamiento automático")
      }
    } catch (error) {
      console.error("Error toggling:", error)
      message.error("Error al alternar el procesamiento automático")
    } finally {
      setSaving(false)
      fetchStatus()
    }
  }

  const handleSaveConfig = async () => {
    try {
      setSaving(true)
      const token = await getToken()
      const data = await updateAutoConfig(token, intervalSeconds, limitPerRun)

      if (data.success) {
        message.success(data.message)
      } else {
        message.error("Error al guardar la configuración")
      }
    } catch (error) {
      console.error("Error saving config:", error)
      message.error("Error al guardar la configuración")
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "40px 0" }}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <Card title="Procesamiento Automático de Presupuestos" style={{ maxWidth: 800, margin: "0 auto" }}>
      <Tabs defaultActiveKey="1">
        <TabPane
          tab={
            <span>
              <MailOutlined />
              Estado
            </span>
          }
          key="1"
        >
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <Alert
              message={
                autoProcessingEnabled
                  ? "Procesamiento Automático ACTIVO"
                  : "Procesamiento Automático INACTIVO"
              }
              description={
                autoProcessingEnabled
                  ? "El sistema está procesando correos con presupuestos automáticamente cada " +
                    intervalSeconds +
                    " segundos."
                  : "El sistema está pausado. Los correos no se procesarán automáticamente."
              }
              type={autoProcessingEnabled ? "success" : "warning"}
              showIcon
            />

            <Card>
              <Descriptions title="Estado del Sistema" column={1} bordered>
                <Descriptions.Item label="Estado">
                  <Tag color={autoProcessingEnabled ? "green" : "red"}>
                    {autoProcessingEnabled ? "ACTIVO" : "INACTIVO"}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Ejecutándose">
                  <Tag color={isRunning ? "green" : "default"}>
                    {isRunning ? "SÍ" : "NO"}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Intervalo">
                  {intervalSeconds} segundos
                </Descriptions.Item>
                <Descriptions.Item label="Correos por ciclo">
                  {limitPerRun} correos
                </Descriptions.Item>
                <Descriptions.Item label="Email Monitoreado">
                  {monitoredEmail}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <Switch
                checked={autoProcessingEnabled}
                onChange={handleToggle}
                loading={saving}
                size="large"
              />
              <div>
                <Text strong style={{ display: "block" }}>
                  Procesamiento Automático
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {autoProcessingEnabled
                    ? "El sistema está procesando correos automáticamente"
                    : "Activa para procesar correos automáticamente"}
                </Text>
              </div>
            </div>
          </Space>
        </TabPane>

        <TabPane
          tab={
            <span>
              <SettingOutlined />
              Configuración
            </span>
          }
          key="2"
        >
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <Card>
              <Title level={4}>Configuración de Procesamiento</Title>

              <div style={{ marginBottom: 24 }}>
                <Text strong>Intervalo de Procesamiento (segundos)</Text>
                <InputNumber
                  min={10}
                  max={3600}
                  value={intervalSeconds}
                  onChange={setIntervalSeconds}
                  style={{ width: "100%", marginTop: 8 }}
                  addonBefore="Cada"
                  addonAfter="segundos"
                />
                <Text type="secondary" style={{ display: "block", marginTop: 4 }}>
                  Tiempo entre cada ciclo de procesamiento de correos
                </Text>
              </div>

              <div style={{ marginBottom: 24 }}>
                <Text strong>Máximo de Correos por Ciclo</Text>
                <InputNumber
                  min={1}
                  max={50}
                  value={limitPerRun}
                  onChange={setLimitPerRun}
                  style={{ width: "100%", marginTop: 8 }}
                  addonAfter="correos"
                />
                <Text type="secondary" style={{ display: "block", marginTop: 4 }}>
                  Número máximo de correos a procesar en cada ciclo
                </Text>
              </div>

              <div style={{ textAlign: "right" }}>
                <Button
                  type="primary"
                  icon={<SettingOutlined />}
                  onClick={handleSaveConfig}
                  loading={saving}
                >
                  Guardar Configuración
                </Button>
              </div>
            </Card>

            <Alert
              message="Recomendaciones"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>Para desarrollo/testing: intervalo de 30-60 segundos</li>
                  <li>Para producción: intervalo de 5-10 minutos</li>
                  <li>El límite de correos por ciclo depende del volumen esperado</li>
                </ul>
              }
              type="info"
              showIcon
            />
          </Space>
        </TabPane>
      </Tabs>
    </Card>
  )
}

export default QuoteProcessingPage

